import { BaseFilterComponent } from './types';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { defineMessages, useIntl } from 'react-intl';
import { Filter } from '../../types';
import { FormBox } from '@lifeomic/chroma-react/components/FormBox';
import { NumericRangeFilterOption } from '../options/types';
import { TextField } from '@lifeomic/chroma-react/components/TextField';
import * as React from 'react';

const messages = defineMessages({
  placeholder: {
    id: 'omicsExplorer.filterResultsManager.components.percentageRange.placeholder',
    defaultMessage: 'Percentage',
  },
  maxOption: {
    id: 'omicsExplorer.filterResultsManager.components.percentageRange.maxOption',
    defaultMessage: '<=',
  },
  minOption: {
    id: 'omicsExplorer.filterResultsManager.components.percentageRange.minOption',
    defaultMessage: '>=',
  },
  addFilter: {
    id: 'omicsExplorer.filterResultsManager.components.percentageRange.addFilter',
    defaultMessage: 'Add Filter',
  },
  friendlyNameSingle: {
    id: 'omicsExplorer.filterResultsManager.components.percentageRange.friendlyNameSingle',
    defaultMessage: '{operator}{value}%',
  },
  friendlyNameRange: {
    id: 'omicsExplorer.filterResultsManager.components.percentageRange.friendlyNameRange',
    defaultMessage: '{min}-{max}%',
  },
});

interface PercentageRangeProps
  extends NumericRangeFilterOption,
    BaseFilterComponent {
  maxHelpMessage?: string;
  minHelpMessage?: string;
}

export const PercentageRange: React.FC<PercentageRangeProps> = ({
  injectedProps: {
    defaultMaximumValue,
    defaultMinimumValue,
    id,
    searchKey,
    step,
  },
  maxHelpMessage,
  minHelpMessage,
  onFilterAdd,
  origin,
}) => {
  const { formatMessage } = useIntl();

  const [minEnteredValue, setMinEnteredValue] = React.useState<number>(
    parseFloat(String(defaultMinimumValue)),
  );
  const [maxEnteredValue, setMaxEnteredValue] = React.useState<number>(
    parseFloat(String(defaultMaximumValue)),
  );

  const handleMinKeyPress = (e: React.ChangeEvent<HTMLInputElement>) =>
    setMinEnteredValue(parseFloat(e.target.value));
  const handleMaxKeyPress = (e: React.ChangeEvent<HTMLInputElement>) =>
    setMaxEnteredValue(parseFloat(e.target.value));

  const handleSubmitClick = () => {
    if (!onFilterAdd) {
      return;
    }

    const minEnteredValueAsDecimal =
      minEnteredValue < 0 ? 0 : minEnteredValue / 100;
    const maxEnteredValueAsDecimal =
      maxEnteredValue > 100 ? 100 : maxEnteredValue / 100;

    const option: Filter = {
      id,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      origin,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      searchKey,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      friendlyName:
        (!isNaN(minEnteredValue) &&
          !isNaN(maxEnteredValue) &&
          formatMessage(messages.friendlyNameRange, {
            min: minEnteredValue,
            max: maxEnteredValue,
          })) ||
        (!isNaN(minEnteredValue) &&
          isNaN(maxEnteredValue) &&
          formatMessage(messages.friendlyNameSingle, {
            value: minEnteredValue,
            operator: formatMessage(messages.minOption),
          })) ||
        (isNaN(minEnteredValue) &&
          !isNaN(maxEnteredValue) &&
          formatMessage(messages.friendlyNameSingle, {
            value: maxEnteredValue,
            operator: formatMessage(messages.maxOption),
          })),
      searchValue:
        (!isNaN(minEnteredValue) &&
          !isNaN(maxEnteredValue) &&
          `${minEnteredValueAsDecimal}-${maxEnteredValueAsDecimal}`) ||
        (!isNaN(minEnteredValue) &&
          isNaN(maxEnteredValue) &&
          `${minEnteredValueAsDecimal}:gte`) ||
        (isNaN(minEnteredValue) &&
          !isNaN(maxEnteredValue) &&
          `${maxEnteredValueAsDecimal}:lte`),
    };
    onFilterAdd(option);

    setMinEnteredValue(parseFloat(String(defaultMinimumValue)));
    setMaxEnteredValue(parseFloat(String(defaultMaximumValue)));
  };

  return (
    <FormBox spacing={1}>
      <TextField
        label={formatMessage(messages.minOption)}
        type="number"
        value={isNaN(minEnteredValue) ? '' : minEnteredValue}
        min={0}
        step={step}
        onChange={handleMinKeyPress}
        helpMessage={minHelpMessage}
        placeholder={formatMessage(messages.placeholder)}
        fullWidth
      />
      <TextField
        label={formatMessage(messages.maxOption)}
        type="number"
        value={isNaN(maxEnteredValue) ? '' : maxEnteredValue}
        min={isNaN(minEnteredValue) ? '' : minEnteredValue}
        max={100}
        onChange={handleMaxKeyPress}
        helpMessage={maxHelpMessage}
        placeholder={formatMessage(messages.placeholder)}
        fullWidth
      />
      <Button
        variant="contained"
        onClick={handleSubmitClick}
        disabled={isNaN(minEnteredValue) && isNaN(maxEnteredValue)}
        fullWidth
      >
        {formatMessage(messages.addFilter)}
      </Button>
    </FormBox>
  );
};
